<template>
  <div class="space-y-4">
    <div class="relative z-30 flex flex-col items-center justify-stretch gap-6 rounded-lg bg-white p-6 drop-shadow-md">
      <div class="flex w-full flex-row items-center justify-start">
        <Button
            class="hover-lift-no-shadow text-left"
            variant="ghost-no-padding"
            @click="redirectToIndex"
        >
          <Icon name="arrow_back_ios" class="w-5 h-5  text-xl text-zinc-800" />
          <span class="ml-1.5 text-sm text-zinc-800">{{ __('Challenges') }}</span>
        </Button>
      </div>

      <div class="flex w-full items-center justify-between relative">
        <div class="flex items-center gap-4">
          <h1 class="text-3xl pr-8 sm:pr-0 font-medium text-zinc-800">{{ challenge.title }}</h1>
          <InputToggle
              :id="`${challenge.id}-active`"
              :value="Boolean(challengeStatus)"
              :class="[
                  isMobile ? 'hidden' : ''
              ]"
              @update:value="toggleActive()"
          />
          <ThreeDotMenu
              :challenge="challenge"
              :permissions="permissions"
              :not-rounded="isMobile"
              @edit="editChallenge"
              @toggle-status="toggleActive"
              @delete="showDeleteModal = true"
              class="sm:hidden absolute top-0 right-0"
          />
        </div>
        <div class="hidden sm:flex items-center gap-2">
          <Button
              variant="outline-no-hover"
              class="hover-lift flex items-center gap-2"
              @click="editChallenge"
          >
            <Icon name="edit" class="mr-2 w-3 h-3 text-primary text-xl" />
            <span class="text-primary">{{ __('Edit') }}</span>
          </Button>
          <Button
              class="hover-lift flex items-center gap-1.5"
              @click="showDeleteModal = true"
          >
            <span class="text-white">{{ __('Delete') }}</span>
          </Button>
        </div>
      </div>

      <div
          v-if="!isMobile"
          class="flex flex-col sm:flex-row w-full justify-between items-start sm:items-center mb-4 gap-3"
      >
        <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:divide-x-2 sm:divide-[#D9D9D9]">
          <div class="flex items-center gap-2">
            <span class="text-sm font-bold text-zinc-800">{{ __('Status:') }}</span>
            <span
                :class="[
            'px-2.5 py-1 text-base rounded-full font-medium',
            challenge.is_reviewed ? 'bg-[#10B981] bg-opacity-10 text-success ' : 'bg-[#EF4444]/10 text-error'
          ]"
            >
          {{ challenge.is_reviewed ? __('Reviewed') : __('Pending Review') }}
        </span>
          </div>
          <div
              v-if="challenge.team_members.length > 1"
              class="flex-none flex items-center gap-2 sm:pl-4"
          >
            <span class="text-sm font-bold">{{ __('Assigned to:')}}</span>
            <div
                :class="
               challenge.team_members.length > 1
                  ? 'flex -space-x-1'
                  : 'flex flex-shrink-0'
              "
            >
              <img
                  v-for="team_member in challenge.team_members"
                  class="block size-6 rounded-full ring-2 ring-white"
                  :src="team_member.profile_picture_url"
                  alt=""
              />
            </div>
            <TeamMembersPopUp class="pl-2" :data="challenge.team_members" />
          </div>
        </div>
        <div v-if="!isMobile"
            class="flex items-center gap-2"
        >
          <button
              class="flex w-full items-center justify-between rounded-lg text-left transition"
              @click="showDetails = !showDetails"
          >
            <span class="font-medium text-sm">{{ __('Challenge Details') }}</span>
            <Icon
                :name="showDetails ? 'expand_less' : 'expand_more'"
                class="ml-1.5 w-5 h-5 text-zinc-600 text-base"
            />
          </button>
        </div>
      </div>
      <div class="flex w-full sm:hidden">
        <button
            class="flex-1 text-center py-2 font-medium border transition-colors duration-150
           rounded-l-lg"
            :class="mobileTab === 'details'
              ? 'bg-secondary text-white border-transparent'
              : 'bg-white text-zinc-600 border-zinc-300'"
            @click="mobileTab = 'details'"
        >
          <span class="text-sm">{{ __('Challenge Details') }}</span>
        </button>

        <button
            class="flex-1 text-center py-2 font-medium border transition-colors duration-150
           rounded-r-lg"
            :class="mobileTab === 'candidates'
              ? 'bg-secondary text-white border-transparent'
              : 'bg-white text-zinc-600 border-zinc-300'"
            @click="mobileTab = 'candidates'"
        >
          <span class="text-sm">{{ __('Candidates') }}</span>
        </button>
      </div>
      <Transition name="fade">
        <div
            v-if="(!isMobile && showDetails) || (isMobile && mobileTab === 'details')"
            class="w-full grid grid-cols-1 gap-y-4"
        >
          <div v-if="isMobile" class="flex flex-col gap-3 sm:hidden">
            <div class="flex items-center gap-2">
              <span class="text-sm font-bold text-zinc-800">{{ __('Status:') }}</span>
              <span
                  :class="[
                    'px-2.5 py-1 text-base rounded-full font-medium',
                    challenge.is_reviewed ? 'bg-[#10B981]/10 text-success' : 'bg-[#EF4444]/10 text-error'
                  ]"
              >
                {{ challenge.is_reviewed ? __('Reviewed') : __('Pending Review') }}
              </span>
            </div>
            <div
                v-if="challenge.team_members.length > 1"
                class="flex-none flex items-center gap-2"
            >
              <span class="text-sm font-bold">{{ __('Assigned to:')}}</span>
              <div
               :class="
                 challenge.team_members.length > 1
                  ? 'flex -space-x-1'
                  : 'flex flex-shrink-0'
                 "
              >
                <img
                    v-for="team_member in challenge.team_members"
                    :key="team_member.id"
                    class="block size-6 rounded-full ring-2 ring-white"
                    :src="team_member.profile_picture_url"
                    alt=""
                />
              </div>
              <TeamMembersPopUp class="pl-2" :data="challenge.team_members" />
            </div>
          </div>
          <div class="flex flex-wrap w-full rounded-2xl border border-zinc-300 p-6 items-center gap-4">
            <div class="flex flex-wrap items-center gap-4">
              <div>
                <span
                    class="chip rounded-app ring-zinc-400 items-center border-0 px-2 py-1 shadow-sm ring-1 ring-inset focus:ring-2 text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="sm:font-medium">{{ challenge.type_label }}</span>
                </span>
              </div>
              <div>
                <span
                    class="chip rounded-app ring-zinc-400 items-center border-0 px-2 py-1 shadow-sm ring-1 ring-inset focus:ring-2 text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="sm:font-medium">{{ challenge.perfect_points }} {{ __('Points')}}</span>
                </span>
              </div>
              <div>
                <span
                    class="chip rounded-app ring-zinc-400 items-center border-0 px-2 py-1 shadow-sm ring-1 ring-inset focus:ring-2 text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed sm:font-medium"
                >
                  {{ __('Created on')}} {{ formatDate(challenge.start_at) || '—' }}
                  <span v-if="!isMobile">{{ __('by')}} {{ challenge.creator }}</span>
                </span>
              </div>
              <div v-if="isMobile">
                <span
                    class="chip rounded-app ring-zinc-400 items-center border-0 px-2 py-1 shadow-sm ring-1 ring-inset focus:ring-2 text-sm sm:leading-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="sm:font-medium">{{ __('Recruiter:')}} {{ challenge.creator }}</span>
                </span>
              </div>
            </div>

            <div class="sm:col-span-2">
              <p class="text-zinc-600 text-base tracking-wider">
            <span v-if="!expanded && challenge.description?.length > 405">
              {{ `${challenge.description.slice(0, 405)}…` }}
              <button class="text-primary underline" @click="expanded = true">
                {{ __('see more') }}
              </button>
            </span>
                <span v-else>{{ challenge.description }}</span>
              </p>
            </div>
            <div class="sm:mb-6 sm:col-span-2 w-full">
              <ExpirationBar
                  :start-date="challenge.start_at"
                  :end-date="challenge.end_at"
                  :never-expires="challenge.never_expires"
              />
            </div>
          </div>
        </div>
      </Transition>

      <DeleteModal
          :open="showDeleteModal"
          :title="__('Delete this challenge?')"
          :message="__('This action will permanently delete the challenge. Are you sure?')"
          confirmText="Yes, Delete"
          cancelText="Cancel"
          @confirm="deleteChallenge"
          @cancel="showDeleteModal = false"
      />
    </div>
    <div v-if="mobileTab === 'candidates' || !isMobile" class="w-full">
      <ChallengeSubmissionsTable
          :challenge-id="challenge.id"
          :submissions="submissions"
          :initial-has-more="hasMoreSubmissions"
          :pipeline-options="availablePipelines"
          :job-options="availableJobs"
          @review-submission="handleReviewSubmission"
          @add-to-pipeline="handleAddToPipeline"
          @invite-to-job="handleInviteToJob"
          @bulk-add-to-pipeline="handleBulkAddToPipeline"
      />
    </div>
  </div>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, reactive, ref, watch} from 'vue';
import axios from 'axios';
import InputToggle from '@/components/form/input-toggle/input-toggle.vue';
import {DeleteModal} from "@/components/other/delete-modal";
import { Button } from '@/components/block/button';
import { Icon } from '@/components/block/icon';
import { __ } from '@/helpers';
import { useToast } from 'vue-toastification';
import TeamMembersPopUp from "@/pages/company/challenges/components/TeamMembersPopUp.vue";
import ExpirationBar from "@/pages/company/challenges/components/ExpirationBar.vue";
import ChallengeSubmissionsTable from "@/pages/company/challenges/components/ChallengeSubmissionsTable.vue";
import ThreeDotMenu from "@/pages/company/challenges/components/ThreeDotMenu.vue";

const props = defineProps({
  challenge: {
    type: Object,
    required: true
  },
  submissions: {
    type: Array,
    default: () => []
  },
  availablePipelines: {
    type: Array,
    default: () => []
  },
  availableJobs: {
    type: Array,
    default: () => []
  },
  hasMoreSubmissions: {
    type: Boolean,
    default: false
  },
  permissions: {
    type: Object,
    required: true
  }
});

const toast = useToast();
const showDeleteModal = ref(false);
const showDetails = ref(false);
const expanded = ref(false);
const showAddToPipelineModal = ref(false);
const showInviteToJobModal = ref(false);
const selectedCandidatesForPipeline = ref([]);
const selectedCandidateForJob = ref(null);
const challengeStatus = ref(Boolean(props.challenge.status));
const permissions = computed(() => props.challenge.permissions || {});

const windowWidth = ref(window.innerWidth);

const isMobile = computed(() => windowWidth.value < 640)
const mobileTab = ref('details');

const handleResize = () => {
  windowWidth.value = window.innerWidth;
}

onMounted(() => {
  window.addEventListener("resize", handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize)
})

watch(() => props.challenge.status, (newStatus) => {
  challengeStatus.value = Boolean(newStatus);
}, {immediate: true});

function redirectToIndex() {
  window.location.href = route('company.challenges');
}

function editChallenge() {
  window.location.href = route('redesign.company.challenges.edit', { challenge: props.challenge.id });
}

async function toggleActive() {
  try {
    const response = await axios.patch(
        route('redesign.company.challenges.toggle-status', { challenge: props.challenge.id }),
        { status: !challengeStatus.value },
    );
    challengeStatus.value = response.data.status;
    toast.success(response.data.message);
  } catch (e) {
    toast.error(e.response?.data?.message || __('Error toggling status'));
  }
}

async function deleteChallenge() {
  showDeleteModal.value = false;
  try {
    const { data } = await axios.delete(
        route('redesign.company.challenges.destroy', { challenge: props.challenge.id })
    );
    toast.success(data.message);
    window.location.href = route('redesign.company.challenges.index');
  } catch (e) {
    toast.error(e.response?.data?.message || __('Error deleting challenge'));
  }
}

const formatDate = (date) => {
  if (!date) return null;
  const [datePart, timePart] = date.split(' ');
  const formattedDate = datePart.split('-').join('.');

  if (isMobile.value) {
    return formattedDate;
  }

  return timePart ? `${formattedDate} ${timePart}` : formattedDate;
};

function handleReviewSubmission(submission) {
  window.location.href = route('redesign.company.challenges.review', {
    challenge: props.challenge.id,
    submission: submission.id
  });
}

function handleAddToPipeline(submission) {
  selectedCandidatesForPipeline.value = [submission];
  showAddToPipelineModal.value = true;
}

function handleInviteToJob(submission) {
  selectedCandidateForJob.value = submission;
  showInviteToJobModal.value = true;
}

function handleBulkAddToPipeline(submissions) {
  selectedCandidatesForPipeline.value = submissions;
  showAddToPipelineModal.value = true;
}
</script>